import type { ReactElement } from "react";

import { cn } from "@/lib/utils";

/**
 * Pill toggle for admin settings (replaces bare checkboxes). Uses neutral “on” state for control-plane UI.
 *
 * Locked (disabled) ON must stay visually distinct from locked OFF — never wash the dark
 * track with opacity-50 (PERMISSIONS-TOGGLE-VISUAL-FIX-1).
 */
export function SettingsToggle({
  id,
  checked,
  disabled,
  onCheckedChange,
  "aria-label": ariaLabel,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (value: boolean) => void;
  "aria-label"?: string;
}): ReactElement {
  return (
    <label
      htmlFor={id}
      data-state={checked ? "on" : "off"}
      data-disabled={disabled ? "true" : "false"}
      className={cn(
        "relative inline-flex items-center",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
      )}
    >
      <input
        id={id}
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onCheckedChange(e.target.checked)}
      />
      <span
        data-testid="settings-toggle-track"
        className={cn(
          "flex h-7 w-12 items-center rounded-full p-0.5 transition-colors",
          "peer-focus-visible:ring-2 peer-focus-visible:ring-neutral-900 peer-focus-visible:ring-offset-2",
          // State-driven track color (not peer-checked alone) so disabled+ON
          // remains a solid dark pill and never collapses into the gray family.
          checked ? "justify-end bg-neutral-900" : "justify-start bg-neutral-200",
          disabled && !checked && "bg-neutral-100",
        )}
      >
        <span className="pointer-events-none h-5 w-5 shrink-0 rounded-full bg-white shadow" />
      </span>
    </label>
  );
}
